/**
 * The API error model (SPEC §57).
 *
 * Mirrors the server's shape so a caller can branch on `code` without parsing
 * a message. `status` is kept because some decisions — retry on 5xx, do not
 * retry on 4xx — are about the HTTP layer, not the domain.
 */

export interface ApiErrorBody {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly details?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  override readonly name = "ApiError";

  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(input: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, unknown> | undefined;
  }) {
    super(input.message);
    this.code = input.code;
    this.status = input.status;
    this.details = input.details;
  }

  /** True when retrying the same request could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.code === "RATE_LIMITED" || this.code === "GITHUB_RATE_LIMIT";
  }

  /**
   * Per-field validation issues, when the server sent any.
   *
   * Returned as a `path -> message` map so a form can put the message next to
   * the input that caused it, rather than in a banner the user has to match up
   * themselves.
   */
  get fieldErrors(): Record<string, string> {
    const issues: unknown = this.details?.issues;

    if (!Array.isArray(issues)) {
      return {};
    }

    const errors: Record<string, string> = {};

    for (const issue of issues as unknown[]) {
      if (isValidationIssue(issue)) {
        errors[issue.path] = issue.message;
      }
    }

    return errors;
  }
}

/** One entry of the server's `details.issues` array. */
interface ValidationIssue {
  readonly path: string;
  readonly message: string;
}

/**
 * Narrow an unknown value to a validation issue.
 *
 * A type predicate rather than a chain of `in` checks, because `details` is
 * free-form JSON: the values are `unknown` until something actually checks
 * them, and asserting otherwise is how a malformed response becomes a runtime
 * error in a component.
 */
function isValidationIssue(value: unknown): value is ValidationIssue {
  if (value === null || typeof value !== "object") {
    return false;
  }

  const candidate = value as { path?: unknown; message?: unknown };

  return typeof candidate.path === "string" && typeof candidate.message === "string";
}

/** Narrow an unknown thrown value to an `ApiError`. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * A message safe to show a user.
 *
 * Anything that is not an `ApiError` is a client-side bug — a thrown
 * `TypeError`, a rejected promise with no reason — and its message would be
 * meaningless to a reader.
 */
export function describeError(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}
